import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Snackbar,
  IconButton,
  Menu,
  MenuItem,
  Collapse,
  Box,
  Stack,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useAuth } from '../../contexts/AuthContext';
import AddStaffDialog from '../../components/forms/AddStaffDialog';
import EditStaffDialog from '../../components/forms/Edit_forms/EditStaffDialog';
import { apiUtils } from '../../utils/api';
import { deleteStaff, getStaffList } from '../../services/staffService';

const flattenErrors = (obj, prefix = '') => {
  if (!obj || typeof obj !== 'object') return [];
  let messages = [];
  Object.entries(obj).forEach(([key, value]) => {
    const label = prefix ? `${prefix}.${key}` : key;
    if (Array.isArray(value)) {
      messages.push(`${label}: ${value.join(', ')}`);
    } else if (typeof value === 'object' && value !== null) {
      messages = messages.concat(flattenErrors(value, label));
    } else if (value) {
      messages.push(`${label}: ${value}`);
    }
  });
  return messages;
};

const formatRole = (value) => {
  const text = (value || '').toString();
  if (!text) return '—';
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const StaffPage = () => {
  const { user } = useAuth();
  const [staffMembers, setStaffMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [expandedStaffId, setExpandedStaffId] = useState(null);

  const fetchStaff = async () => {
    setLoading(true);
    setError('');
    try {
      const list = await getStaffList();
      setStaffMembers(Array.isArray(list) ? list : []);
    } catch (err) {
      setError('Failed to load staff list.');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchStaff();
  }, []);

  const handleMenuOpen = (event, staff) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedStaff(staff);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleEditClick = () => {
    setEditOpen(true);
    handleMenuClose();
  };

  const handleViewClick = () => {
    if (selectedStaff) {
      const id = selectedStaff.staff_id || selectedStaff.id;
      setExpandedStaffId((prev) => (prev === id ? null : id));
    }
    handleMenuClose();
  };

  const handleDeleteClick = async () => {
    handleMenuClose();
    if (!selectedStaff) return;
    const confirmDelete = window.confirm(`Delete ${selectedStaff.staff_name || selectedStaff.user?.username}?`);
    if (!confirmDelete) return;
    try {
      await deleteStaff(selectedStaff.staff_id || selectedStaff.id);
      setSnackbar({ open: true, message: 'Staff member deleted.', severity: 'success' });
      fetchStaff();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to delete staff member.', severity: 'error' });
    }
  };

  const handleAddStaff = async (form) => {
    try {
      await apiUtils.post('register/staff/', {
        username: form.username,
        email: form.email,
        password: form.password,
        first_name: form.first_name,
        last_name: form.last_name,
        staff_name: form.staff_name || `${form.first_name} ${form.last_name}`.trim(),
        type_of_career: form.type_of_career,
        qualification: form.qualification,
      });
      setSnackbar({ open: true, message: 'Staff member added successfully!', severity: 'success' });
      setAddOpen(false);
      fetchStaff();
    } catch (err) {
      let message = 'Failed to add staff member';
      const data = err.response?.data;
      if (data) {
        if (typeof data === 'string') {
          message = data;
        } else if (typeof data === 'object') {
          message = flattenErrors(data).join(' | ');
        }
      }
      setSnackbar({ open: true, message, severity: 'error' });
      throw new Error(message);
    }
  };

  const renderActions = (staff) => {
    if (user?.role !== 'admin') return null;
    return (
      <IconButton onClick={(event) => handleMenuOpen(event, staff)}>
        <VisibilityIcon />
      </IconButton>
    );
  };

  return (
    <Container>
      <Typography variant="h4" gutterBottom>
        Staff Management
      </Typography>
      <Button variant="contained" color="primary" sx={{ mb: 2 }} onClick={() => setAddOpen(true)}>
        Add Staff
      </Button>
      <Paper sx={{ p: 2 }}>
        {loading ? (
          <CircularProgress />
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Username</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Qualification</TableCell>
                  <TableCell>Email</TableCell>
                  {user?.role === 'admin' && <TableCell>Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {staffMembers.map((staff) => {
                  const staffId = staff.staff_id || staff.id;
                  const isExpanded = expandedStaffId === staffId;
                  return (
                    <React.Fragment key={staffId}>
                      <TableRow>
                        <TableCell>{staffId}</TableCell>
                        <TableCell>{staff.staff_name || `${staff.user?.first_name || ''} ${staff.user?.last_name || ''}`.trim()}</TableCell>
                        <TableCell>{staff.user?.username || '—'}</TableCell>
                        <TableCell>{formatRole(staff.type_of_career)}</TableCell>
                        <TableCell>{staff.qualification || '—'}</TableCell>
                        <TableCell>{staff.user?.email || '—'}</TableCell>
                        {user?.role === 'admin' && <TableCell>{renderActions(staff)}</TableCell>}
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={user?.role === 'admin' ? 7 : 6} sx={{ py: 0 }}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ py: 2, px: 1, backgroundColor: 'rgba(0,0,0,0.02)', borderRadius: 1 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                Staff Details
                              </Typography>
                              <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} flexWrap="wrap">
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    Full Name
                                  </Typography>
                                  <Typography variant="body2">
                                    {`${staff.user?.first_name || ''} ${staff.user?.last_name || ''}`.trim() || '—'}
                                  </Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    Username
                                  </Typography>
                                  <Typography variant="body2">{staff.user?.username || '—'}</Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    Role
                                  </Typography>
                                  <Typography variant="body2">{formatRole(staff.type_of_career)}</Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    Qualification
                                  </Typography>
                                  <Typography variant="body2">{staff.qualification || '—'}</Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    Email
                                  </Typography>
                                  <Typography variant="body2">{staff.user?.email || '—'}</Typography>
                                </Box>
                              </Stack>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleViewClick}>
          <VisibilityIcon fontSize="small" sx={{ mr: 1 }} />
          View
        </MenuItem>
        <MenuItem onClick={handleEditClick}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDeleteClick}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      <AddStaffDialog open={addOpen} onClose={() => setAddOpen(false)} onAdd={handleAddStaff} />
      <EditStaffDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSuccess={fetchStaff}
        initialData={selectedStaff}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        message={snackbar.message}
      />
    </Container>
  );
};

export default StaffPage;
